import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  VStack, 
  HStack, 
  Icon, 
  Divider, 
  Spinner, 
  Avatar 
} from '@chakra-ui/react';
import { 
  FiActivity, 
  FiGlobe,
  FiCpu,
  FiServer,
  FiTarget
} from 'react-icons/fi';
import { timeAgo } from '../utils/formatters';

// Enhanced HTML decoder function
const decodeHTMLEntities = (html) => {
  if (!html) return '';
  
  // Create a temporary element to decode HTML entities
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  const decoded = txt.value;
  

  return decoded;
};

// Simple HTML parser for decoded content
const parseDecodedHTML = (htmlContent) => {
  if (!htmlContent) return null;
  

  // Split by <br> or <br /> tags first
  const lines = htmlContent.split(/<br\s*\/?>/gi);
  
  return lines.map((line, lineIndex) => {
    if (!line.trim()) {
      return <Box key={`empty-${lineIndex}`} h="8px" />; // Empty line
    }
    
    // Process <strong> and <b> tags in each line
    const processHTMLLine = (text) => {
      const parts = [];
      let lastIndex = 0;
      
      // Regex for <strong>, <b>, and markdown *bold*
      const htmlRegex = /(<strong>([^<]+)<\/strong>)|(<b>([^<]+)<\/b>)|(\*([^*]+)\*)/gi;
      let match;
      
      while ((match = htmlRegex.exec(text)) !== null) {
        // Add text before the tag
        if (match.index > lastIndex) {
          const beforeText = text.slice(lastIndex, match.index);
          if (beforeText.trim()) {
            parts.push(
              <span key={`text-${lineIndex}-${parts.length}`} style={{ color: '#d1d5db' }}>
                {beforeText}
              </span>
            );
          }
        }
        
        // Add bold text (from <strong>, <b>, or *bold*)
        const boldText = match[2] || match[4] || match[6];
        if (boldText) {
        
          parts.push(
            <span key={`bold-${lineIndex}-${parts.length}`} style={{ fontWeight: 'bold', color: 'white' }}>
              {boldText}
            </span>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        const remainingText = text.slice(lastIndex);
        if (remainingText.trim()) {
          parts.push(
            <span key={`remaining-${lineIndex}-${parts.length}`} style={{ color: '#d1d5db' }}>
              {remainingText}
            </span>
          );
        }
      }
      
      // If no HTML tags found, return as plain text
      if (parts.length === 0) {
        return <span style={{ color: '#d1d5db' }}>{text}</span>;
      }
      
      return parts;
    };
    
    return (
      <div key={`line-${lineIndex}`} style={{ fontSize: '14px', marginBottom: '4px' }}>
        {processHTMLLine(line)}
      </div>
    );
  });
};



const ActivityFeed = ({ activities = [], isLoading = false, isFullHeight = false }) => {

  // Filter valid activities without hiding any specific types
  const validActivities = Array.isArray(activities) ? activities.filter(a => a && (a.id || a.type || a.message_type)) : [];
  

  return (
    <Box 
      h={isFullHeight ? "100%" : "auto"} 
      display="flex" 
      flexDirection="column" 
      position="relative"
      overflow="hidden"
    >
      {/* Content area with scroll */}
      <Box 
        flex="1" 
        overflow="hidden" 
        borderRadius="xl" 
        h="100%"
        display="flex"
        flexDirection="column"
      >
        {isLoading && !activities ? (
          <Flex justify="center" align="center" h={isFullHeight ? "100%" : "200px"} flex="1">
            <VStack spacing={4}>
              <Spinner size="xl" color="green.400" thickness="4px" />
              <Text color="gray.400">Loading AI activities...</Text>
            </VStack>
          </Flex>
        ) : validActivities.length === 0 ? (
          <Flex 
            direction="column" 
            align="center" 
            justify="center" 
            h={isFullHeight ? "100%" : "200px"}
            border="2px dashed" 
            borderColor="rgba(78, 224, 53, 0.3)" 
            borderRadius="xl"
            bg="rgba(0, 0, 0, 0.2)"
            transition="all 0.3s ease"
            className="empty-state"
            _hover={{
              borderColor: "rgba(78, 224, 53, 0.5)",
              bg: "rgba(0, 0, 0, 0.3)"
            }}
            flex="1"
          >
            <Box className="empty-state-icon">
              <Icon as={FiActivity} boxSize={6} color="green.400" />
            </Box>
            <Text color="white" fontSize="lg" fontWeight="medium" mb={2}>No Recent Activity</Text>
            <Text color="gray.400" fontSize="sm" textAlign="center" maxW="md">
              Wells AI is analyzing markets. Activities will appear here.
            </Text>
          </Flex>
        ) : (
          <Box
            overflowY="auto"
            h="100%"
            maxH="none"
            pr={2}
            id="aiActivityScrollContainer"
            className="custom-scrollbar"
            flex="1"
            pb={4}
            position="relative"
          >
            <VStack spacing={3} align="stretch" divider={<Divider borderColor="rgba(255, 255, 255, 0.1)" />}>
              {validActivities.map((activity, index) => (
                <ActivityItem key={activity.id || index} activity={activity} />
              ))}
            </VStack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ActivityItem = ({ activity }) => {
  // Function to get icon component based on type
  const getIconByType = (activity) => {
    const messageType = String(activity.message_type || '').toLowerCase();
    const type = String(activity.type || '').toLowerCase();
    const message = String(activity.message || activity.formatted_message || '').toLowerCase();
    
    // Bitget/Exchange related - Trading/Exchange icon
    if (messageType.includes('bitget') || type.includes('bitget') || message.includes('bitget')) {
      return FiServer; // Server icon for exchange updates
    }
    
    // Session related - Globe icon for session changes
    if (messageType.includes('session') || type.includes('session') || message.includes('session')) {
      return FiGlobe; // Globe icon for global market sessions
    }
    
    // Market related - Target icon for market status
    if (messageType.includes('market') || type.includes('market') || message.includes('market')) {
      return FiTarget; // Target icon for market targeting
    }
    
    // Performance/AI related - CPU icon for AI processing
    if (message.includes('performance') || message.includes('profit') || message.includes('loss') || message.includes('%')) {
      return FiCpu; // CPU icon for AI/processing
    }
    
    // Default - Activity icon
    return FiActivity;
  };
  
  // Get activity color scheme based on type - Enhanced green theme colors
  const getActivityColor = (activity) => {
    const messageType = String(activity.message_type || '').toLowerCase();
    const type = String(activity.type || '').toLowerCase();
    const message = String(activity.message || activity.formatted_message || '').toLowerCase();
    
    // Bitget/Exchange - Emerald green for trading
    if (messageType.includes('bitget') || type.includes('bitget') || message.includes('bitget')) {
      return 'emerald';
    }
    
    // Session - Teal for global sessions
    if (messageType.includes('session') || type.includes('session') || message.includes('session')) {
      return 'teal';
    }
    
    // Market - Green for market updates
    if (messageType.includes('market') || type.includes('market') || message.includes('market')) {
      return 'green';
    }
    
    // Performance/AI - Different greens based on profit/loss
    if (message.includes('performance') || message.includes('profit') || message.includes('loss') || message.includes('%')) {
      // Check for profit/loss to determine exact green shade
      if (message.includes('profit') || message.includes('+')) {
        return 'green'; // Bright green for profits
      } else if (message.includes('loss') || message.includes('-')) {
        return 'orange'; // Orange for losses (still warm, not harsh red)
      } else {
        return 'cyan'; // Cyan for neutral performance updates
      }
    }
    
    // Default - Light green
    return 'green';
  };
  
  // Get activity title based on type - Enhanced with emojis
  const getActivityTitle = (activity) => {
    const messageType = String(activity.message_type || '').toLowerCase();
    const type = String(activity.type || '').toLowerCase();
    const message = String(activity.message || activity.formatted_message || '').toLowerCase();
    
    // Bitget/Exchange
    if (messageType.includes('bitget') || type.includes('bitget') || message.includes('bitget')) {
      return '🏦 Exchange Update';
    }
    
    // Session
    if (messageType.includes('session') || type.includes('session') || message.includes('session')) {
      return '🌍 Session Update';
    }
    
    // Market
    if (messageType.includes('market') || type.includes('market') || message.includes('market')) {
      return '📊 Market Update';
    }
    
    // Performance/AI
    if (message.includes('performance') || message.includes('profit') || message.includes('loss') || message.includes('%')) {
      return '🤖 AI Update';
    }
    
    // Default
    return '💡 System Update';
  };
  
  const colorScheme = getActivityColor(activity);
  const IconComponent = getIconByType(activity);
  const activityTitle = getActivityTitle(activity);
  
  // Clean up message and decode HTML entities first
  const rawMessage = activity.message || activity.formatted_message || '';
  const decodedMessage = decodeHTMLEntities(rawMessage);
  const hasHTML = /<[^>]*>/g.test(decodedMessage);
  const hasMarkdown = /\*[^*]+\*/g.test(decodedMessage);
  const needsFormatting = hasHTML || hasMarkdown;
  
  
  return (
    <Box py={3} className="ai-activity-message">
      <Flex justify="space-between" mb={1}>
        <HStack spacing={3}>
          <Avatar 
            size="sm" 
            bg={`${colorScheme}.500`} 
            icon={<Icon as={IconComponent} color="white" />}
            boxShadow={`0 0 20px ${colorScheme === 'emerald' ? 'rgba(16, 185, 129, 0.4)' : 
                                   colorScheme === 'teal' ? 'rgba(20, 184, 166, 0.4)' :
                                   colorScheme === 'green' ? 'rgba(34, 197, 94, 0.4)' :
                                   colorScheme === 'cyan' ? 'rgba(8, 145, 178, 0.4)' :
                                   'rgba(251, 146, 60, 0.4)'}`}
            border="0px solid"
            borderColor={`${colorScheme}.300`}
            _hover={{
              transform: 'scale(1.1)',
              transition: 'all 0.2s ease'
            }}
          />
          <Text fontWeight="medium" color="white" fontSize="sm">
            {activityTitle}
          </Text>
        </HStack>
        
        <Text fontSize="xs" color="gray.500">
          {activity.time_ago || timeAgo(activity.created_at)}
        </Text>
      </Flex>
      
      {/* Message Content - Decode HTML entities first, then parse */}
      <Box pl={10} className="ai-activity-message-content">
        {needsFormatting ? (
          parseDecodedHTML(decodedMessage)
        ) : (
          <Text fontSize="sm" color="gray.300">
            {decodedMessage || activity.description || "System update received"}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default ActivityFeed;